// Temporary: remove with the custom_project_stages flag clean-up
// Exists for localStorage and saved filters written before stage filters used stage ids: those hold stage profile
// names (RUNNING, DONE...) in `projectStage`, which now holds stage ids while profile names live in `projectStageProfile`.
import {cloneDeep, isEqual, isPlainObject} from 'lodash';
import Util from '../../util/util';
import type {StageProfile} from '../../util/projectStageUtil';

type FilterSection = Record<string, unknown>;
type FilterObject = Record<string, FilterSection | null | undefined>;

export const STAGE_PROFILE_NAMES: ReadonlyArray<StageProfile> = ['OPPORTUNITY', 'PLANNING', 'RUNNING', 'HALTED', 'DONE'];

const STAGE_KEY = 'projectStage';
const PROFILE_KEY = 'projectStageProfile';
const STAGE_OPERATORS_KEY = 'projectStage_operators';
const PROFILE_OPERATORS_KEY = 'projectStageProfile_operators';

const isProfileName = (value: unknown): boolean => STAGE_PROFILE_NAMES.includes(value as StageProfile);

// Matches graphql's moveProfileValuesToProfileKey and service-company's SavedFilterStageProfiles: projectStage_operators
// is copied to projectStageProfile_operators only when the section has none yet, and is never overwritten, since it wins
// over projectStageProfile_operators (projectStageConditionPredicate). A projectStage left empty goes with its operators.
const normaliseSection = (section: FilterSection): FilterSection => {
	const stageValues = Array.isArray(section[STAGE_KEY]) ? (section[STAGE_KEY] as unknown[]) : [];
	const moved = stageValues.filter(isProfileName);
	if (moved.length === 0) {
		return section;
	}
	const next = cloneDeep(section);
	const profiles = Array.isArray(next[PROFILE_KEY]) ? [...(next[PROFILE_KEY] as unknown[])] : [];
	moved.forEach(value => {
		if (!profiles.includes(value)) {
			profiles.push(value);
		}
	});
	next[PROFILE_KEY] = profiles;
	if (STAGE_OPERATORS_KEY in next && !(PROFILE_OPERATORS_KEY in next)) {
		next[PROFILE_OPERATORS_KEY] = cloneDeep(next[STAGE_OPERATORS_KEY]);
	}
	const kept = stageValues.filter(value => !isProfileName(value));
	if (kept.length > 0) {
		next[STAGE_KEY] = kept;
	} else {
		delete next[STAGE_KEY];
		delete next[STAGE_OPERATORS_KEY];
	}
	return next;
};

/** Returns the same reference when nothing changes, otherwise a new object (input not mutated). */
export function normaliseProjectStageFilters<T extends FilterObject | null | undefined>(filters: T): T {
	if (!isPlainObject(filters)) {
		return filters;
	}
	const source = filters as FilterObject;
	let changed = false;
	const result: FilterObject = {};
	Object.keys(source).forEach(name => {
		const section = source[name];
		if (isPlainObject(section)) {
			const current = section as FilterSection;
			const next = normaliseSection(current);
			if (isEqual(next, current)) {
				result[name] = current;
			} else {
				result[name] = next;
				changed = true;
			}
		} else {
			result[name] = section;
		}
	});
	return changed ? (result as T) : filters;
}

/** Reads and parses the key, normalises it, writes it back only if it changed. Missing, 'undefined' or invalid JSON -> null. */
export function readNormalisedStoredFilters(storageKey: string): FilterObject | null {
	const raw = Util.localStorageGetItem(storageKey);
	if (!raw || raw === 'undefined') {
		return null;
	}
	let parsed: FilterObject | null;
	try {
		parsed = JSON.parse(raw);
	} catch (e) {
		return null;
	}
	if (!parsed || typeof parsed !== 'object') {
		return null;
	}
	const normalised = normaliseProjectStageFilters(parsed);
	if (normalised !== parsed) {
		Util.localStorageSetItem(storageKey, JSON.stringify(normalised));
	}
	return normalised;
}
